import React, { useState, useEffect, useContext } from 'react';
import api from '../api';
import { AuthContext } from '../AuthContext';
import HOAFilesRequest from './HOAFilesRequest';
import BasicRedactionModal from './BasicRedactionModal';

const HOAFilesPro = () => {
  const { user } = useContext(AuthContext);
  const [activeTab, setActiveTab] = useState('hoa-docs');
  const [hoaDocs, setHoaDocs] = useState([]);
  const [homeownerFiles, setHomeownerFiles] = useState([]);
  const [properties, setProperties] = useState([]);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [subcategoryFilter, setSubcategoryFilter] = useState('all');
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showTransferInModal, setShowTransferInModal] = useState(false);
  const [showPasswordPrompt, setShowPasswordPrompt] = useState(false);
  const [showManualFolderModal, setShowManualFolderModal] = useState(false);
  const [showUploadToPropertyModal, setShowUploadToPropertyModal] = useState(false);
  const [showTransferToPropertyModal, setShowTransferToPropertyModal] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [passwordInput, setPasswordInput] = useState('');
  const [allDocuments, setAllDocuments] = useState([]);
  const [manualFolders, setManualFolders] = useState([]);
  const [manualFolderData, setManualFolderData] = useState({
    address: '',
    lot_number: '',
    notes: ''
  });
  const [uploadToPropertyData, setUploadToPropertyData] = useState({
    property_address: '',
    file: null,
    title: '',
    description: ''
  });
  const [editData, setEditData] = useState({
    id: '',
    title: '',
    subcategory: '',
    role_access: [],
    password: '',
    description: '',
    file: null
  });
  const [uploadData, setUploadData] = useState({
    title: '',
    subcategory: 'hoa_documents',
    role_access: ['all_board'],
    password: '',
    description: '',
    file: null,
    show_in_documents: false  // NEW: checkbox to push to Documents tab
  });
  
  const [customCategories, setCustomCategories] = useState([]);
  const [showAddCategoryInput, setShowAddCategoryInput] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [showManageCategoriesModal, setShowManageCategoriesModal] = useState(false);
  const [selectedDocsForBulk, setSelectedDocsForBulk] = useState([]);
  const [showBulkEditModal, setShowBulkEditModal] = useState(false);
  const [bulkEditCategory, setBulkEditCategory] = useState('');
  const [showRedactionModal, setShowRedactionModal] = useState(false);
  const [redactionDocId, setRedactionDocId] = useState(null);
  const [redactionDocTitle, setRedactionDocTitle] = useState('');

  const isBoard = ['board_member', 'president', 'vice_president', 'treasurer', 'secretary', 'platform_admin'].includes(user?.role?.toLowerCase());

  useEffect(() => {
    if (activeTab === 'hoa-docs') {
      loadHOADocuments();
    } else if (activeTab === 'homeowner-files') {
      loadProperties();
    }
  }, [activeTab]);

  useEffect(() => {
    loadCustomCategories();
  }, []);

  const loadCustomCategories = async () => {
    // Load custom categories from API (not localStorage)
    try {
      const response = await api.get('/api/hoa-files-pro/categories');
      const categories = response.data.categories || [];
      setCustomCategories(categories);
    } catch (error) {
      console.error('Failed to load custom categories:', error);
    }
  };

  const saveCustomCategory = async (categoryName) => {
    try {
      await api.post('/api/hoa-files-pro/categories', { name: categoryName });
      // Reload categories
      await loadCustomCategories();
    } catch (error) {
      console.error('Failed to save category:', error);
      alert('Error saving category: ' + (error.response?.data?.detail || error.message));
    }
  };

  const deleteCustomCategory = async (categoryName) => {
    try {
      await api.delete(`/api/hoa-files-pro/categories/${encodeURIComponent(categoryName)}`);
      // Reload categories
      await loadCustomCategories();
    } catch (error) {
      console.error('Failed to delete category:', error);
      alert('Error deleting category: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleBulkCategoryUpdate = async () => {
    if (!bulkEditCategory) {
      alert('Please select a category');
      return;
    }

    if (!confirm(`Update category for ${selectedDocsForBulk.length} document(s) to "${bulkEditCategory}"?`)) {
      return;
    }

    try {
      setLoading(true);
      
      // Update each selected document
      for (const docId of selectedDocsForBulk) {
        await api.put(`/api/hoa-files-pro/update-document/${docId}`, {
          subcategory: bulkEditCategory
        });
      }

      alert(`✅ Updated ${selectedDocsForBulk.length} document(s)`);
      setSelectedDocsForBulk([]);
      setShowBulkEditModal(false);
      setBulkEditCategory('');
      loadHOADocuments();
    } catch (error) {
      alert('Error: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const loadHOADocuments = async () => {
    if (!isBoard) return;
    
    try {
      setLoading(true);
      const response = await api.get('/api/hoa-files-pro/hoa-documents');
      setHoaDocs(response.data.documents || []);
    } catch (error) {
      console.error('Error loading HOA documents:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadAllDocuments = async () => {
    try {
      const response = await api.get('/api/documents');
      setAllDocuments(response.data.documents || []);
    } catch (error) {
      console.error('Error loading all documents:', error);
    }
  };

  const handleViewDocument = async (doc) => {
    console.log('🔵 handleViewDocument called with doc:', doc);
    
    // Check if password protected
    if (doc.password || doc.requires_password) {
      console.log('🔒 Document is password protected, showing prompt');
      setSelectedDoc(doc);
      setShowPasswordPrompt(true);
      return;
    }
    
    // View directly - use document_id if id doesn't exist
    const docId = doc.id || doc.document_id;
    console.log('📄 Viewing document, ID:', docId);
    
    // Detect if mobile (iOS, Android)
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    
    try {
      // Backend returns EITHER JSON (new) or Blob (old)
      const response = await api.get(`/api/documents/${docId}/view`, { responseType: 'blob' });
      
      // NEW DOCUMENTS: Backend returns JSON with presigned URL
      if (response.data instanceof Blob && response.data.type === 'application/json') {
        const text = await response.data.text();
        const jsonData = JSON.parse(text);
        if (jsonData.url) {
          console.log('✅ Got presigned URL (NEW document)');
          
          if (isMobile) {
            // Mobile: Navigate in same tab to avoid popup blocker
            window.location.href = jsonData.url;
          } else {
            // Desktop: Open in new window
            const newWindow = window.open(jsonData.url, '_blank', 'noopener,noreferrer');
            if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
              // Fallback: navigate in same tab if popup blocked
              window.location.href = jsonData.url;
            }
          }
          return;
        }
      }
      
      // OLD DOCUMENTS: Backend returns file directly (FileResponse)
      console.log('✅ Got file directly (OLD document)');
      const blob = response.data instanceof Blob ? response.data : new Blob([response.data]);
      const objectUrl = URL.createObjectURL(blob);
      
      if (isMobile) {
        // Mobile: Navigate in same tab
        window.location.href = objectUrl;
      } else {
        // Desktop: Try to open in new window
        const newWindow = window.open(objectUrl, '_blank', 'noopener,noreferrer');
        if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
          // Fallback: navigate in same tab if popup blocked
          window.location.href = objectUrl;
        } else {
          // Clean up blob URL after a delay
          setTimeout(() => URL.revokeObjectURL(objectUrl), 5000);
        }
      }
    } catch (error) {
      console.error('❌ Error:', error);
      alert('Error viewing document: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handlePasswordSubmit = async () => {
    console.log('🔐 Password submitted for document:', selectedDoc.title);
    console.log('🔐 Entered password:', passwordInput);
    console.log('🔐 Expected password:', selectedDoc.password);
    
    if (passwordInput !== selectedDoc.password) {
      alert('❌ Incorrect password');
      return;
    }
    
    console.log('✅ Password correct, closing modal and viewing document');
    setShowPasswordPrompt(false);
    setPasswordInput('');
    
    // View document with mobile-friendly fallback
    try {
      const docId = selectedDoc.id || selectedDoc.document_id;
      console.log('📄 Fetching document with ID:', docId);
      
      // Backend now returns JSON with presigned URL
      const response = await api.get(`/api/documents/${docId}/view`);
      console.log('✅ Got response:', response.data);
      
      if (response.data && response.data.url) {
        const presignedUrl = response.data.url;
        console.log('📎 Presigned URL received');
        
        // Try to open in new window/tab
        const newWindow = window.open(presignedUrl, '_blank', 'noopener,noreferrer');
        
        // Check if popup was blocked
        if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
          console.log('⚠️ Popup blocked');
          alert('Please allow popups for this site to view documents');
        } else {
          console.log('✅ Document opened in new tab');
        }
      } else {
        throw new Error('Invalid response format from server');
      }
    } catch (error) {
      console.error('❌ Error viewing document:', error);
      alert('Error viewing document: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleTransferOut = async (docId) => {
    console.log('📤 handleTransferOut called with docId:', docId);
    // Find the document to check if password protected
    const doc = hoaDocs.find(d => d.id === docId || d.document_id === docId);
    if (!doc) {
      console.log('❌ Document not found in hoaDocs');
      return;
    }
    
    console.log('📄 Found document:', doc.title);
    
    // If password protected, verify password first
    let passwordPrompt = null;
    if (doc.password || doc.requires_password) {
      console.log('🔒 Document is password protected, requesting password');
      passwordPrompt = prompt('🔒 This document is password protected.\n\nEnter password to transfer:');
      
      if (passwordPrompt === null) {
        console.log('❌ User cancelled password prompt');
        return; // User cancelled
      }
      console.log('✅ Password entered, proceeding with transfer confirmation');
    }
    
    if (!confirm('⚠️ TRANSFER DOCUMENT TO REGULAR DOCUMENTS TAB?\n\nIMPORTANT:\n• Document will be removed from HOA Files Pro\n• PASSWORD PROTECTION WILL BE REMOVED\n• Document will be visible in Documents tab to all users\n\nDocuments tab does not support password protection.\n\nDo you want to proceed?')) {
      console.log('❌ User cancelled transfer confirmation');
      return;
    }
    
    console.log('🔄 Attempting to transfer document...');
    try {
      setLoading(true);
      const fd = new FormData();
      if (passwordPrompt) {
        fd.append('pwd', passwordPrompt);
      }
      await api.post(`/api/hoa-files-pro/transfer-out/${docId}`, fd);
      console.log('✅ Document transferred successfully');
      alert('✅ Document transferred to Documents tab');
      loadHOADocuments();
    } catch (error) {
      console.log('❌ Error transferring document:', error);
      alert('Error: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleEditDocument = (doc) => {
    console.log('✏️ handleEditDocument called with doc:', doc);
    // If password protected, verify password first
    let passwordPrompt = null;
    if (doc.password || doc.requires_password) {
      console.log('🔒 Document is password protected, requesting password');
      passwordPrompt = prompt('🔒 This document is password protected.\n\nEnter password to edit:');
      
      if (passwordPrompt === null) {
        console.log('❌ User cancelled password prompt');
        return; // User cancelled
      }
      console.log('✅ Password entered, opening edit modal');
    }
    
    setEditData({
      id: doc.id || doc.document_id,
      title: doc.title,
      subcategory: doc.subcategory || 'hoa_documents',
      role_access: doc.role_access || ['all_board'],
      password: doc.password || '',
      description: doc.description || '',
      file: null,
      pwd: passwordPrompt  // Store password for verification
    });
    setShowEditModal(true);
    console.log('📝 Edit modal should now be open');
  };

  const handleSaveEdit = async () => {
    try {
      setLoading(true);
      
      // Always use FormData to support pwd parameter
      const formData = new FormData();
      if (editData.file) formData.append('file', editData.file);
      formData.append('title', editData.title);
      formData.append('subcategory', editData.subcategory);
      formData.append('role_access', JSON.stringify(editData.role_access));
      if (editData.password) formData.append('password', editData.password);
      if (editData.description) formData.append('description', editData.description);
      if (editData.pwd) formData.append('pwd', editData.pwd);  // Add password verification
      
      await api.put(`/api/hoa-files-pro/update-document/${editData.id}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      alert('✅ Document updated successfully');
      setShowEditModal(false);
      loadHOADocuments();
    } catch (error) {
      alert('Error updating document: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteDocument = async (docId) => {
    console.log('🗑️ handleDeleteDocument called with docId:', docId);
    // Find the document to check if password protected
    const doc = hoaDocs.find(d => d.id === docId || d.document_id === docId);
    if (!doc) {
      console.log('❌ Document not found in hoaDocs');
      return;
    }
    
    console.log('📄 Found document:', doc.title);
    
    // If password protected, verify password first
    let passwordPrompt = null;
    if (doc.password || doc.requires_password) {
      console.log('🔒 Document is password protected, requesting password');
      passwordPrompt = prompt('🔒 This document is password protected.\n\nEnter password to delete:');
      
      if (passwordPrompt === null) {
        console.log('❌ User cancelled password prompt');
        return; // User cancelled
      }
      console.log('✅ Password entered, proceeding with delete confirmation');
    }
    
    if (!confirm('⚠️ DELETE DOCUMENT?\n\nThis will permanently delete the document from HOA Files Pro.\n\nAre you sure?')) {
      console.log('❌ User cancelled delete confirmation');
      return;
    }
    
    console.log('🔄 Attempting to delete document...');
    try {
      const fd = new FormData();
      if (passwordPrompt) {
        fd.append('pwd', passwordPrompt);
      }
      await api.delete(`/api/hoa-files-pro/delete-document/${docId}`, { data: fd });
      console.log('✅ Document deleted successfully');
      alert('✅ Document deleted');
      loadHOADocuments();
    } catch (error) {
      console.log('❌ Error deleting document:', error);
      alert('Error deleting document: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleTransferIn = async (docId) => {
    try {
      setLoading(true);
      await api.post(`/api/hoa-files-pro/transfer-in/${docId}`);
      alert('✅ Document transferred to HOA Files Pro');
      setShowTransferInModal(false);
      loadHOADocuments();
    } catch (error) {
      alert('Error: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleRedactDocument = (doc) => {
    const docId = doc.id || doc.document_id;
    const docTitle = doc.title || 'Document';
    console.log('🔒 Opening redaction modal for:', docTitle, docId);
    setRedactionDocId(docId);
    setRedactionDocTitle(docTitle);
    setShowRedactionModal(true);
  };

  const loadProperties = async () => {
    if (!isBoard) return;
    
    try {
      setLoading(true);
      const response = await api.get('/api/hoa-files-pro/properties');
      setProperties(response.data.properties || []);
      
      // Load manual folders
      const manualResponse = await api.get('/api/hoa-files-pro/manual-folders');
      setManualFolders(manualResponse.data.folders || []);
    } catch (error) {
      console.error('Error loading properties:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateManualFolder = async () => {
    if (!manualFolderData.address) {
      alert('Please enter an address');
      return;
    }
    
    try {
      await api.post('/api/hoa-files-pro/create-manual-folder', manualFolderData);
      alert('✅ Manual folder created');
      setShowManualFolderModal(false);
      setManualFolderData({ address: '', lot_number: '', notes: '' });
      loadProperties();
    } catch (error) {
      console.error('Error creating manual folder:', error);
      alert('Error: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleRefreshAndLink = async () => {
    if (!confirm('Refresh and link accounts?\n\nThis will scan for manual folders that now have matching property management accounts and ask you to link or keep separate.')) {
      return;
    }
    
    try {
      setLoading(true);
      const response = await api.post('/api/hoa-files-pro/refresh-and-link');
      
      if (response.data.matches && response.data.matches.length > 0) {
        // Show matches and ask user to link or rename
        for (const match of response.data.matches) {
          const action = confirm(
            `Found match!\n\n` +
            `Manual Folder: ${match.manual_address}\n` +
            `Property Account: ${match.property_address}\n` +
            `Homeowner: ${match.homeowner_name}\n\n` +
            `Click OK to LINK & MERGE (future docs auto-tag)\n` +
            `Click CANCEL to RENAME manual folder as "${match.manual_address} -1"`
          );
          
          if (action) {
            // Link and merge
            await api.post('/api/hoa-files-pro/link-folder', {
              manual_folder_id: match.manual_folder_id,
              homeowner_id: match.homeowner_id
            });
          } else {
            // Rename to -1
            await api.post('/api/hoa-files-pro/rename-manual-folder', {
              manual_folder_id: match.manual_folder_id,
              new_name: `${match.manual_address} -1`
            });
          }
        }
        
        alert(`✅ Processed ${response.data.matches.length} matches`);
        loadProperties();
      } else {
        alert('No matches found. All manual folders are either already linked or have no corresponding property accounts.');
      }
    } catch (error) {
      alert('Error: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleUploadToProperty = async () => {
    if (!uploadToPropertyData.property_address || !uploadToPropertyData.file) {
      alert('Please select property and file');
      return;
    }
    
    try {
      setLoading(true);
      const formData = new FormData();
      formData.append('file', uploadToPropertyData.file);
      formData.append('property_address', uploadToPropertyData.property_address);
      formData.append('title', uploadToPropertyData.title);
      formData.append('description', uploadToPropertyData.description || '');
      
      await api.post('/api/hoa-files-pro/upload-to-property', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      alert('✅ Document uploaded to property folder');
      setShowUploadToPropertyModal(false);
      setUploadToPropertyData({ property_address: '', file: null, title: '', description: '' });
    } catch (error) {
      alert('Error: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const loadHomeownerFiles = async (propertyAddress = null, homeownerId = null) => {
    try {
      setLoading(true);
      const params = {};
      if (propertyAddress) params.property_address = propertyAddress;
      if (homeownerId) params.homeowner_id = homeownerId;
      
      const response = await api.get('/api/hoa-files-pro/homeowner-files', { params });
      setHomeownerFiles(response.data.documents || []);
    } catch (error) {
      console.error('Error loading homeowner files:', error);
    } finally {
      setLoading(false);
    }
  };

  const runRetroactiveTagging = async () => {
    if (!confirm('Run retroactive tagging?\n\nThis will scan all meeting minutes and violations to tag homeowners. This may take a moment.')) {
      return;
    }

    try {
      setLoading(true);
      const response = await api.post('/api/hoa-files-pro/retroactive-tag');
      alert(`✅ Tagged ${response.data.tagged_count} documents successfully!`);
    } catch (error) {
      console.error('Error running retroactive tagging:', error);
      alert('Failed to run retroactive tagging: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const filteredProperties = properties.filter(prop => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      prop.address?.toLowerCase().includes(search) ||
      prop.lot_number?.toLowerCase().includes(search) ||
      prop.homeowners?.some(h => h.name?.toLowerCase().includes(search))
    );
  });

  if (!isBoard) {
    return (
      <div className="p-8 text-center">
        <h2 className="text-2xl font-semibold text-gray-900 mb-4">HOA Files Pro</h2>
        <p className="text-gray-600">This feature is available to board members only.</p>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">🗂️ HOA Files Pro</h1>
        <p className="text-gray-600 mt-2">Advanced document management with role-based access and homeowner files</p>
      </div>

      {/* Tabs */}
      <div className="mb-6 border-b border-gray-200">
        <nav className="flex gap-4">
          <button
            onClick={() => setActiveTab('hoa-docs')}
            className={`px-4 py-2 border-b-2 font-medium transition-colors ${
              activeTab === 'hoa-docs'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-600 hover:text-gray-900'
            }`}
          >
            🔒 HOA Documents
          </button>
          <button
            onClick={() => setActiveTab('homeowner-files')}
            className={`px-4 py-2 border-b-2 font-medium transition-colors ${
              activeTab === 'homeowner-files'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-600 hover:text-gray-900'
            }`}
          >
            📁 Homeowner Files
          </button>
          <button
            onClick={() => setActiveTab('file-requests')}
            className={`px-4 py-2 border-b-2 font-medium transition-colors ${
              activeTab === 'file-requests'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-600 hover:text-gray-900'
            }`}
          >
            📋 HOA Files Request
          </button>
        </nav>
      </div>

      {/* HOA Documents Tab */}
      {activeTab === 'hoa-docs' && (
        <div className="space-y-6">
          <div className="bg-white rounded-xl p-6 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold">Board-Only Documents</h2>
              <div className="flex gap-2">
                <button
                  onClick={() => {
                    loadAllDocuments();
                    setShowTransferInModal(true);
                  }}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                >
                  📥 Transfer In
                </button>
                <button
                  onClick={() => setShowUploadModal(true)}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  ➕ Upload Document
                </button>
              </div>
            </div>
            
            <p className="text-sm text-gray-600 mb-4">
              Store sensitive board documents with role-based access. These files are never visible to homeowners.
            </p>

            {/* Subcategory Filter and Bulk Actions */}
            <div className="mb-4 flex flex-col sm:flex-row gap-3 items-stretch sm:items-end">
              <div className="flex-1 min-w-[200px]">
                <label className="block text-sm font-medium text-gray-700 mb-2">Filter by Category</label>
                <select
                  value={subcategoryFilter}
                  onChange={(e) => setSubcategoryFilter(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="all">All Documents</option>
                  <option value="hoa_documents">HOA Documents</option>
                  <option value="templates">📋 Templates</option>
                  <option value="transition_files">Transition Files</option>
                  {customCategories.map((cat, idx) => (
                    <option key={idx} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => setShowManageCategoriesModal(true)}
                  className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 text-sm whitespace-nowrap"
                >
                  🏷️ Manage Categories
                </button>
                {selectedDocsForBulk.length > 0 && (
                  <button
                    onClick={() => setShowBulkEditModal(true)}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm flex items-center gap-2 whitespace-nowrap"
                  >
                    <span>✏️</span>
                    <span>Edit {selectedDocsForBulk.length} Selected</span>
                  </button>
                )}
                <button
                  onClick={() => {
                    const filteredDocs = hoaDocs.filter(doc => subcategoryFilter === 'all' || doc.subcategory === subcategoryFilter);
                    const allSelected = filteredDocs.every(doc => selectedDocsForBulk.includes(doc.id));
                    if (allSelected) {
                      // Deselect all filtered docs
                      setSelectedDocsForBulk(selectedDocsForBulk.filter(id => !filteredDocs.some(doc => doc.id === id)));
                    } else {
                      // Select all filtered docs
                      const newSelections = filteredDocs.map(doc => doc.id).filter(id => !selectedDocsForBulk.includes(id));
                      setSelectedDocsForBulk([...selectedDocsForBulk, ...newSelections]);
                    }
                  }}
                  className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 text-sm whitespace-nowrap"
                >
                  {(() => {
                    const filteredDocs = hoaDocs.filter(doc => subcategoryFilter === 'all' || doc.subcategory === subcategoryFilter);
                    const allSelected = filteredDocs.every(doc => selectedDocsForBulk.includes(doc.id));
                    return allSelected ? '☐ Deselect All' : '☑️ Select All';
                  })()}
                </button>
                {selectedDocsForBulk.length > 0 && (
                  <button
                    onClick={() => setSelectedDocsForBulk([])}
                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm whitespace-nowrap"
                  >
                    🗑️ Clear Selection
                  </button>
                )}
              </div>
            </div>

            {loading ? (
              <div className="text-center py-8">
                <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto"></div>
                <p className="text-gray-600 mt-2">Loading...</p>
              </div>
            ) : hoaDocs.length === 0 ? (
              <div className="text-center py-8 bg-gray-50 rounded-lg">
                <p className="text-gray-600">No HOA documents yet. Upload your first document.</p>
              </div>
            ) : (
              <div className="space-y-2">
                {hoaDocs
                  .filter(doc => subcategoryFilter === 'all' || doc.subcategory === subcategoryFilter)
                  .map((doc) => (
                  <div key={doc.id} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 relative">
                    {/* Checkbox for bulk selection */}
                    <input
                      type="checkbox"
                      checked={selectedDocsForBulk.includes(doc.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedDocsForBulk([...selectedDocsForBulk, doc.id]);
                        } else {
                          setSelectedDocsForBulk(selectedDocsForBulk.filter(id => id !== doc.id));
                        }
                      }}
                      className="absolute top-3 left-3 w-5 h-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <div className="flex items-start justify-between ml-7">
                      <div className="flex-1">
                        <h3 className="font-medium text-gray-900">{doc.title}</h3>
                        <div className="flex gap-3 mt-1 text-sm text-gray-600">
                          <span>Category: {doc.subcategory || 'General'}</span>
                          <span>•</span>
                          <span>Access: {doc.role_access?.join(', ') || 'All Board'}</span>
                          {doc.password && <span>🔒 Password Protected</span>}
                        </div>
                        <div className="text-xs text-gray-500 mt-1 font-mono">
                          ID: {doc.id?.substring(0, 8)}... 
                          <button
                            onClick={() => {
                              navigator.clipboard.writeText(doc.id);
                              alert('Document ID copied to clipboard!');
                            }}
                            className="ml-2 text-blue-600 hover:text-blue-800"
                          >
                            📋 Copy Full ID
                          </button>
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <button
                          onClick={() => handleViewDocument(doc)}
                          className="px-2 py-1 md:px-3 bg-blue-600 text-white text-xs md:text-sm rounded hover:bg-blue-700 whitespace-nowrap"
                        >
                          👁️ View
                        </button>
                        <button
                          onClick={() => handleEditDocument(doc)}
                          className="px-2 py-1 md:px-3 bg-yellow-600 text-white text-xs md:text-sm rounded hover:bg-yellow-700 whitespace-nowrap"
                        >
                          ✏️ Edit
                        </button>
                        <button
                          onClick={() => handleTransferOut(doc.id || doc.document_id)}
                          className="px-2 py-1 md:px-3 bg-orange-600 text-white text-xs md:text-sm rounded hover:bg-orange-700 whitespace-nowrap"
                        >
                          📤 Out
                        </button>
                        <button
                          onClick={() => handleDeleteDocument(doc.id || doc.document_id)}
                          className="px-2 py-1 md:px-3 bg-red-600 text-white text-xs md:text-sm rounded hover:bg-red-700 whitespace-nowrap"
                        >
                          🗑️ Del
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Homeowner Files Tab */}
      {activeTab === 'homeowner-files' && (
        <div className="space-y-6">
          <div className="bg-white rounded-xl p-6 shadow-sm">
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center sm:justify-between gap-3 mb-4">
              <h2 className="text-xl font-semibold">Homeowner Files</h2>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => setShowManualFolderModal(true)}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 whitespace-nowrap text-sm"
                >
                  ➕ Manual Folder
                </button>
                <button
                  onClick={handleRefreshAndLink}
                  disabled={loading}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 whitespace-nowrap text-sm"
                >
                  🔗 Refresh & Link
                </button>
                <button
                  onClick={() => setShowUploadToPropertyModal(true)}
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 whitespace-nowrap text-sm"
                >
                  📤 Upload to Property
                </button>
                <button
                  onClick={runRetroactiveTagging}
                  disabled={loading}
                  className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:bg-gray-400 whitespace-nowrap text-sm"
                >
                  🔄 Retroactive Tag
                </button>
              </div>
            </div>

            {/* Search */}
            <div className="mb-4">
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by address, lot, or name..."
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Properties Grid */}
            {loading ? (
              <div className="text-center py-8">
                <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto"></div>
                <p className="text-gray-600 mt-2">Loading properties...</p>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {/* Manual Folders - Offline/Unregistered */}
                {manualFolders.map((folder, index) => (
                  <div
                    key={`manual-${index}`}
                    className="border-2 border-dashed border-yellow-300 bg-yellow-50 rounded-lg p-4"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="font-medium text-gray-900">{folder.address}</div>
                        <div className="text-xs text-yellow-700 mt-1">📝 Manual Folder (No Account)</div>
                        {folder.lot_number && (
                          <div className="text-sm text-gray-600 mt-1">Lot: {folder.lot_number}</div>
                        )}
                        {folder.notes && (
                          <div className="text-xs text-gray-500 mt-1">{folder.notes}</div>
                        )}
                      </div>
                      <button
                        onClick={() => {
                          setSelectedProperty(folder);
                          loadHomeownerFiles(null, folder.id);
                        }}
                        className="text-yellow-700 hover:text-yellow-900 text-sm"
                      >
                        Open →
                      </button>
                    </div>
                  </div>
                ))}
                
                {/* Regular Properties */}
                {filteredProperties.map((property, index) => (
                  <button
                    key={index}
                    onClick={() => {
                      setSelectedProperty(property);
                      loadHomeownerFiles(property.address);
                    }}
                    className="border border-gray-200 rounded-lg p-4 text-left hover:bg-blue-50 hover:border-blue-300 transition-colors"
                  >
                    <div className="font-medium text-gray-900">{property.address}</div>
                    {property.lot_number && (
                      <div className="text-sm text-gray-600 mt-1">Lot: {property.lot_number}</div>
                    )}
                    <div className="text-sm text-gray-600 mt-2">
                      {property.homeowners?.length || 0} resident(s)
                    </div>
                    {property.homeowners?.map((h, i) => (
                      <div key={i} className="text-xs text-gray-500">{h.name}</div>
                    ))}
                  </button>
                ))}
              </div>
            )}

            {/* Selected Property Files */}
            {selectedProperty && (
              <div className="mt-6 bg-gray-50 rounded-lg p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold">
                    📂 Files for {selectedProperty.address}
                  </h3>
                  <button
                    onClick={() => setSelectedProperty(null)}
                    className="text-gray-600 hover:text-gray-900"
                  >
                    ✕ Close
                  </button>
                </div>

                {homeownerFiles.length === 0 ? (
                  <div className="text-center py-8 text-gray-600">
                    No files tagged to this property yet.
                  </div>
                ) : (
                  <div className="space-y-2">
                    {homeownerFiles.map((doc) => (
                      <div key={doc.id} className="bg-white border border-gray-200 rounded-lg p-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <h4 className="font-medium text-gray-900">{doc.title}</h4>
                            <p className="text-sm text-gray-600 mt-1">
                              {doc.category} • {new Date(doc.uploaded_at || doc.created_at).toLocaleDateString()}
                            </p>
                          </div>
                          <button
                            onClick={() => handleViewDocument(doc)}
                            className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700"
                          >
                            View
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Password Prompt Modal */}
      {showPasswordPrompt && selectedDoc && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full">
            <h3 className="text-xl font-semibold mb-4">🔒 Password Required</h3>
            <p className="text-sm text-gray-600 mb-4">
              This document is password protected. Please enter the password to view.
            </p>
            
            <input
              type="password"
              value={passwordInput}
              onChange={(e) => setPasswordInput(e.target.value)}
              placeholder="Enter password"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg mb-4"
              onKeyPress={(e) => e.key === 'Enter' && handlePasswordSubmit()}
            />
            
            <div className="flex gap-3">
              <button
                onClick={handlePasswordSubmit}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Unlock
              </button>
              <button
                onClick={() => {
                  setShowPasswordPrompt(false);
                  setPasswordInput('');
                  setSelectedDoc(null);
                }}
                className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 max-w-lg w-full">
            <h3 className="text-xl font-semibold mb-4">✏️ Edit Document</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Document Title *</label>
                <input
                  type="text"
                  value={editData.title}
                  onChange={(e) => setEditData({...editData, title: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  placeholder="Enter document title"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Category *</label>
                <select
                  value={editData.subcategory}
                  onChange={(e) => setEditData({...editData, subcategory: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                >
                  <option value="hoa_documents">HOA Documents</option>
                  <option value="templates">Templates</option>
                  <option value="transition_files">Transition Files</option>
                  <option value="template_library">Template Library</option>
                  {customCategories.map((cat, idx) => (
                    <option key={idx} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Role Access *</label>
                <div className="space-y-2">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={editData.role_access.includes('all_board')}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setEditData({...editData, role_access: ['all_board']});
                        }
                      }}
                      className="mr-2"
                    />
                    <span className="text-sm">All Board Members</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={editData.role_access.includes('president')}
                      onChange={(e) => {
                        const roles = e.target.checked 
                          ? [...editData.role_access.filter(r => r !== 'all_board'), 'president']
                          : editData.role_access.filter(r => r !== 'president');
                        setEditData({...editData, role_access: roles});
                      }}
                      className="mr-2"
                    />
                    <span className="text-sm">President Only</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={editData.role_access.includes('secretary')}
                      onChange={(e) => {
                        const roles = e.target.checked 
                          ? [...editData.role_access.filter(r => r !== 'all_board'), 'secretary']
                          : editData.role_access.filter(r => r !== 'secretary');
                        setEditData({...editData, role_access: roles});
                      }}
                      className="mr-2"
                    />
                    <span className="text-sm">Secretary Only</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={editData.role_access.includes('treasurer')}
                      onChange={(e) => {
                        const roles = e.target.checked 
                          ? [...editData.role_access.filter(r => r !== 'all_board'), 'treasurer']
                          : editData.role_access.filter(r => r !== 'treasurer');
                        setEditData({...editData, role_access: roles});
                      }}
                      className="mr-2"
                    />
                    <span className="text-sm">Treasurer Only</span>
                  </label>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Password (Optional)</label>
                <input
                  type="password"
                  value={editData.password}
                  onChange={(e) => setEditData({...editData, password: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  placeholder="Leave blank for no password"
                />
                <p className="text-xs text-gray-500 mt-1">Password protect this file</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Description (Optional)</label>
                <textarea
                  value={editData.description}
                  onChange={(e) => setEditData({...editData, description: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  rows="3"
                  placeholder="Enter description"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Replace File (Optional)</label>
                <input
                  type="file"
                  onChange={(e) => setEditData({...editData, file: e.target.files[0]})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
                <p className="text-xs text-gray-500 mt-1">Leave blank to keep existing file</p>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={handleSaveEdit}
                disabled={loading || !editData.title}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400"
              >
                {loading ? 'Saving...' : 'Save Changes'}
              </button>
              <button
                onClick={() => {
                  setShowEditModal(false);
                  setEditData({ id: '', title: '', subcategory: '', role_access: [], password: '', description: '', file: null });
                }}
                disabled={loading}
                className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Transfer In Modal */}
      {showTransferInModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 max-w-2xl w-full max-h-[80vh] overflow-y-auto">
            <h3 className="text-xl font-semibold mb-4">📥 Transfer Documents to HOA Files Pro</h3>
            <p className="text-sm text-gray-600 mb-4">
              Select documents from the main Documents tab to add to HOA Files Pro. They will remain in both locations.
            </p>
            
            {loading ? (
              <div className="text-center py-8">
                <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto"></div>
              </div>
            ) : (
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {allDocuments
                  .filter(doc => !doc.document_category || doc.document_category !== 'hoa_files_pro')
                  .map((doc) => (
                    <div key={doc.id} className="border border-gray-200 rounded-lg p-3 hover:bg-gray-50">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <h4 className="font-medium text-gray-900 text-sm">{doc.title}</h4>
                          <p className="text-xs text-gray-600">{doc.category}</p>
                        </div>
                        <button
                          onClick={() => handleTransferIn(doc.id)}
                          className="px-3 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-700"
                        >
                          Transfer In
                        </button>
                      </div>
                    </div>
                  ))}
              </div>
            )}
            
            <div className="flex justify-end mt-4">
              <button
                onClick={() => setShowTransferInModal(false)}
                className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Manual Folder Modal */}
      {showManualFolderModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full">
            <h3 className="text-xl font-semibold mb-4">➕ Create Manual Folder</h3>
            <p className="text-sm text-gray-600 mb-4">
              Create a folder for offline users or those without accounts. Files can be linked later when account is created.
            </p>
            
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Property Address *</label>
                <input
                  type="text"
                  value={manualFolderData.address}
                  onChange={(e) => setManualFolderData({...manualFolderData, address: e.target.value})}
                  placeholder="e.g., 123 Main St"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Lot Number (Optional)</label>
                <input
                  type="text"
                  value={manualFolderData.lot_number}
                  onChange={(e) => setManualFolderData({...manualFolderData, lot_number: e.target.value})}
                  placeholder="e.g., Lot 45"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Notes (Optional)</label>
                <textarea
                  value={manualFolderData.notes}
                  onChange={(e) => setManualFolderData({...manualFolderData, notes: e.target.value})}
                  placeholder="e.g., Offline user, no email"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  rows="2"
                />
              </div>
            </div>
            
            <div className="flex gap-3 mt-4">
              <button
                onClick={handleCreateManualFolder}
                className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
              >
                Create
              </button>
              <button
                onClick={() => {
                  setShowManualFolderModal(false);
                  setManualFolderData({ address: '', lot_number: '', notes: '' });
                }}
                className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Upload to Property Modal */}
      {showUploadToPropertyModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full">
            <h3 className="text-xl font-semibold mb-4">📤 Upload Document to Property</h3>
            
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Select Property *</label>
                <select
                  value={uploadToPropertyData.property_address}
                  onChange={(e) => setUploadToPropertyData({...uploadToPropertyData, property_address: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                >
                  <option value="">-- Select Property --</option>
                  {properties.map((prop, i) => (
                    <option key={i} value={prop.address}>{prop.address} (Lot {prop.lot_number})</option>
                  ))}
                  {manualFolders.map((folder, i) => (
                    <option key={`m-${i}`} value={folder.address}>{folder.address} - Manual Folder</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Document Title *</label>
                <input
                  type="text"
                  value={uploadToPropertyData.title}
                  onChange={(e) => setUploadToPropertyData({...uploadToPropertyData, title: e.target.value})}
                  placeholder="Enter title"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">File *</label>
                <input
                  type="file"
                  onChange={(e) => setUploadToPropertyData({...uploadToPropertyData, file: e.target.files[0]})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>
            </div>
            
            <div className="flex gap-3 mt-4">
              <button
                onClick={handleUploadToProperty}
                disabled={loading}
                className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:bg-gray-400"
              >
                {loading ? 'Uploading...' : 'Upload'}
              </button>
              <button
                onClick={() => {
                  setShowUploadToPropertyModal(false);
                  setUploadToPropertyData({ property_address: '', file: null, title: '', description: '' });
                }}
                className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 max-w-lg w-full">
            <h3 className="text-xl font-semibold mb-4">Upload HOA Document</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Document Title *</label>
                <input
                  type="text"
                  value={uploadData.title}
                  onChange={(e) => setUploadData({...uploadData, title: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  placeholder="Enter document title"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Category *</label>
                <select
                  value={uploadData.subcategory}
                  onChange={(e) => setUploadData({...uploadData, subcategory: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                >
                  <option value="hoa_documents">HOA Documents</option>
                  <option value="templates">Templates</option>
                  <option value="transition_files">Transition Files</option>
                  <option value="template_library">Template Library</option>
                  {customCategories.map((cat, idx) => (
                    <option key={idx} value={cat}>{cat}</option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">Use "Manage Categories" to add new categories</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Role Access *</label>
                <div className="space-y-2">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={uploadData.role_access.includes('all_board')}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setUploadData({...uploadData, role_access: ['all_board']});
                        }
                      }}
                      className="mr-2"
                    />
                    <span className="text-sm">All Board Members</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={uploadData.role_access.includes('president')}
                      onChange={(e) => {
                        const roles = e.target.checked 
                          ? [...uploadData.role_access.filter(r => r !== 'all_board'), 'president']
                          : uploadData.role_access.filter(r => r !== 'president');
                        setUploadData({...uploadData, role_access: roles});
                      }}
                      className="mr-2"
                    />
                    <span className="text-sm">President Only</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={uploadData.role_access.includes('secretary')}
                      onChange={(e) => {
                        const roles = e.target.checked 
                          ? [...uploadData.role_access.filter(r => r !== 'all_board'), 'secretary']
                          : uploadData.role_access.filter(r => r !== 'secretary');
                        setUploadData({...uploadData, role_access: roles});
                      }}
                      className="mr-2"
                    />
                    <span className="text-sm">Secretary Only</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={uploadData.role_access.includes('treasurer')}
                      onChange={(e) => {
                        const roles = e.target.checked 
                          ? [...uploadData.role_access.filter(r => r !== 'all_board'), 'treasurer']
                          : uploadData.role_access.filter(r => r !== 'treasurer');
                        setUploadData({...uploadData, role_access: roles});
                      }}
                      className="mr-2"
                    />
                    <span className="text-sm">Treasurer Only</span>
                  </label>
                </div>
              </div>

              {/* Show in Documents Tab Checkbox */}
              <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={uploadData.show_in_documents}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setUploadData({
                        ...uploadData, 
                        show_in_documents: checked,
                        password: checked ? '' : uploadData.password  // Clear password if showing in documents
                      });
                    }}
                    className="mt-1 w-5 h-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <div className="flex-1">
                    <div className="font-medium text-gray-900">Also show in Documents tab</div>
                    <div className="text-xs text-gray-600 mt-1">
                      {uploadData.show_in_documents ? (
                        <span className="text-orange-700">
                          ⚠️ Document will be visible in both HOA Files Pro AND Documents tab. Password protection will be disabled.
                        </span>
                      ) : (
                        <span className="text-green-700">
                          ✅ Document stays ONLY in HOA Files Pro with password protection
                        </span>
                      )}
                    </div>
                  </div>
                </label>
              </div>

              {/* Password Protection - Only available if NOT showing in documents */}
              {!uploadData.show_in_documents && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Password Protection (Optional)
                  </label>
                  <input
                    type="password"
                    value={uploadData.password}
                    onChange={(e) => setUploadData({...uploadData, password: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    placeholder="Leave empty for no password"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    If set, users will need this password to view the document
                  </p>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">File *</label>
                <input
                  type="file"
                  onChange={(e) => setUploadData({...uploadData, file: e.target.files[0]})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={async () => {
                  if (!uploadData.title || !uploadData.file) {
                    alert('Please enter title and select a file');
                    return;
                  }
                  
                  try {
                    setLoading(true);
                    const formData = new FormData();
                    formData.append('file', uploadData.file);
                    formData.append('title', uploadData.title);
                    formData.append('subcategory', uploadData.subcategory);
                    formData.append('role_access', JSON.stringify(uploadData.role_access));
                    formData.append('show_in_documents', uploadData.show_in_documents);
                    if (uploadData.password) formData.append('password', uploadData.password);
                    if (uploadData.description) formData.append('description', uploadData.description);
                    
                    await api.post('/api/hoa-files-pro/upload-document', formData, {
                      headers: { 'Content-Type': 'multipart/form-data' }
                    });
                    
                    alert('Document uploaded successfully!');
                    setShowUploadModal(false);
                    setUploadData({ title: '', subcategory: 'hoa_documents', role_access: ['all_board'], password: '', description: '', file: null, show_in_documents: false });
                    loadHOADocuments();
                  } catch (error) {
                    alert('Upload failed: ' + (error.response?.data?.detail || error.message));
                  } finally {
                    setLoading(false);
                  }
                }}
                disabled={loading || !uploadData.title || !uploadData.file}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400"
              >
                {loading ? 'Uploading...' : 'Upload'}
              </button>
              <button
                onClick={() => {
                  setShowUploadModal(false);
                  setUploadData({ title: '', subcategory: 'hoa_documents', role_access: ['all_board'], password: '', description: '', file: null });
                }}
                disabled={loading}
                className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Manage Categories Modal */}
      {showManageCategoriesModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full">
            <h3 className="text-xl font-semibold mb-4">🏷️ Manage Categories</h3>
            <p className="text-sm text-gray-600 mb-4">
              Add or remove custom categories for HOA documents. These will appear in both the filter dropdown and upload forms.
            </p>
            
            {/* Add New Category */}
            <div className="mb-4">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={newCategoryName}
                  onChange={(e) => setNewCategoryName(e.target.value)}
                  placeholder="Enter new category name..."
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && newCategoryName.trim()) {
                      saveCustomCategory(newCategoryName.trim());
                      setNewCategoryName('');
                    }
                  }}
                />
                <button
                  onClick={() => {
                    if (newCategoryName.trim()) {
                      saveCustomCategory(newCategoryName.trim());
                      setNewCategoryName('');
                    }
                  }}
                  disabled={!newCategoryName.trim()}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm hover:bg-green-700 disabled:bg-gray-400"
                >
                  ➕ Add
                </button>
              </div>
            </div>

            {/* Existing Categories */}
            <div className="mb-4">
              <h4 className="text-sm font-medium text-gray-700 mb-2">Custom Categories</h4>
              {customCategories.length === 0 ? (
                <p className="text-sm text-gray-500 italic">No custom categories yet</p>
              ) : (
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {customCategories.map((category, idx) => (
                    <div key={idx} className="flex items-center justify-between bg-gray-50 px-3 py-2 rounded-lg">
                      <span className="text-sm text-gray-900">{category}</span>
                      <button
                        onClick={() => {
                          if (confirm(`Delete category "${category}"?\n\nThis won't affect existing documents using this category.`)) {
                            deleteCustomCategory(category);
                          }
                        }}
                        className="text-red-600 hover:text-red-800 text-sm"
                      >
                        🗑️
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Default Categories Info */}
            <div className="mb-4 p-3 bg-blue-50 rounded-lg">
              <h4 className="text-sm font-medium text-blue-900 mb-1">Default Categories</h4>
              <p className="text-xs text-blue-700">HOA Documents, Templates, Transition Files, Template Library</p>
            </div>
            
            <div className="flex justify-end">
              <button
                onClick={() => {
                  setShowManageCategoriesModal(false);
                  setNewCategoryName('');
                }}
                className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Edit Modal */}
      {showBulkEditModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full">
            <h3 className="text-xl font-semibold mb-4">✏️ Bulk Edit Documents</h3>
            <p className="text-sm text-gray-600 mb-4">
              Update the category for {selectedDocsForBulk.length} selected document(s).
            </p>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">New Category *</label>
              <select
                value={bulkEditCategory}
                onChange={(e) => setBulkEditCategory(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">-- Select Category --</option>
                <option value="hoa_documents">HOA Documents</option>
                <option value="templates">📋 Templates</option>
                <option value="transition_files">Transition Files</option>
                <option value="template_library">Template Library</option>
                {customCategories.map((cat, idx) => (
                  <option key={idx} value={cat}>{cat}</option>
                ))}
              </select>
            </div>
            
            <div className="flex gap-3">
              <button
                onClick={handleBulkCategoryUpdate}
                disabled={loading || !bulkEditCategory}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400"
              >
                {loading ? 'Updating...' : 'Update Category'}
              </button>
              <button
                onClick={() => {
                  setShowBulkEditModal(false);
                  setBulkEditCategory('');
                }}
                disabled={loading}
                className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* HOA Files Request Tab */}
      {activeTab === 'file-requests' && (
        <HOAFilesRequest />
      )}

      {/* Basic Redaction Modal */}
      {showRedactionModal && (
        <BasicRedactionModal
          docId={redactionDocId}
          docTitle={redactionDocTitle}
          onClose={() => {
            setShowRedactionModal(false);
            setRedactionDocId(null);
            setRedactionDocTitle('');
          }}
        />
      )}
    </div>
  );
};

export default HOAFilesPro;
